import classNames from 'classnames';
import React from 'react';

import {TABLE_COLUMN, TableDimension, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import ColumnHeaderAxisCell from 'notebook/tables/core-ui/ColumnHeaderAxisCell';
import {TableRowProps} from 'notebook/tables/core-ui/TableRow';
import {countTableRender, makeCountedIsEqual} from 'notebook/tables/perf/TableInstrumentation';
import {AxisCellModelProvider} from 'notebook/tables/store/contexts/AxisCellModelContext';
import {useTableModel} from 'notebook/tables/store/hooks/useTableModel';
import {computeIsAxisGhostCell} from 'notebook/tables/store/logic/TableDimensionsLogic';
import TableUtil from 'notebook/tables/TableUtil';
import {ElementMouseEvent} from 'util/Types';

export interface TableHeaderRowProps
  // The rendering slice is defined on TableRow; pick it off rather than repeating it here.
  extends Pick<TableRowProps, 'sliceStart'> {
  // Rendering slice (see TableRow.js)
  sliceEnd: number | null;

  // Table actions
  axisCellDragOverHandler?: (index: TableIndex, e: ElementMouseEvent) => void;
  axisCellDragStartHandler?: (options: {start: TableIndex; active: string}, e: ElementMouseEvent) => void;
  axisCellDragHandleMouseDown?: (dim: TableDimension, index: number) => boolean;
  finalizeAndExitEditMode?: () => void;
  freezeThroughColumn?: (j: number) => void;
  unfreezeFromColumn?: (j: number) => void;
}

/**
 * The header row in a TableEditable, containing the maximize icon and Excel column labels (A, B, C, ...)
 */
function TableHeaderRow({
  sliceStart,
  sliceEnd,
  axisCellDragOverHandler,
  axisCellDragStartHandler,
  axisCellDragHandleMouseDown,
  finalizeAndExitEditMode,
  freezeThroughColumn,
  unfreezeFromColumn,
}: TableHeaderRowProps): JSX.Element | null {
  countTableRender('TableHeaderRow');
  // The header row renders one cell per column plus the table's leading cell, so it subscribes to
  // the column count and to that leading cell rather than to the table itself: an edit anywhere in
  // the table's contents leaves both untouched and so does not re-render the header row.
  const {tableModel: columnCount} = useTableModel((model) => model.dimensions.columnCount);
  const {tableModel: rowHeaderCell} = useTableModel((model) => model.props.table._rowHeader);
  // Table-level configuration is identical for every column header, so it is read from the table
  // model rather than threaded through props by TableEditable._getHeaderTRProps. Only the
  // `columnHeader` slice is subscribed to so that unrelated model changes do not re-render the
  // header row.
  const {tableModel: columnHeader} = useTableModel((model) => model.columnHeader);
  // The leading cell's shadow is table-level; the per-column consequences of the affixed
  // configuration are derived on each column header's own model.
  const {tableModel: affixed} = useTableModel((model) => model.affixed);
  const {tableModel: firstColumnWidth} = useTableModel((model) => model.measurements.firstColumnWidth);
  const {ghostCellsCount, isLeftAlignRowHeader} = columnHeader;

  function renderCell(j: number): JSX.Element {
    const isGhostCell = computeIsAxisGhostCell(TABLE_COLUMN, j, columnCount);

    return (
      <AxisCellModelProvider key={`axis-${j}`} dim={TABLE_COLUMN} index={j}>
        <ColumnHeaderAxisCell
          {...(!isGhostCell && {
            cellDragStart: axisCellDragStartHandler,
            cellDragOver: axisCellDragOverHandler,
            dragHandleMouseDown: axisCellDragHandleMouseDown,
          })}
          dataTestAttrs={{component: 'TableHeaderRow', element: 'columnLabelCell'}}
          finalizeAndExitEditMode={finalizeAndExitEditMode}
          freezeThroughColumn={freezeThroughColumn}
          unfreezeFromColumn={unfreezeFromColumn}
        />
      </AxisCellModelProvider>
    );
  }

  function renderLeadingCell(): JSX.Element | string {
    if (rowHeaderCell == null) {
      return (
        <td
          key="left-col-filter"
          className={classNames(
            'mediocre-tableEditable-axisCell mediocre-tableEditable-axisCell--header cell-sticky',
            {
              'align-left': isLeftAlignRowHeader,
              'cell-rightShadow': affixed.showAffixedShadow && affixed.affixedColumnCount === 0,
            }
          )}
        />
      );
    }
    return rowHeaderCell;
  }

  return (
    <tr className={classNames({'mediocre-tableEditable-headerRow--noAxis': firstColumnWidth === 0})}>
      {TableUtil.mapColumns({
        sliceStart,
        sliceEnd,

        ghostCellsCount,
        normalCellsCount: columnCount,

        mapLeadingCell: () => renderLeadingCell(),
        mapNormalCell: (j: number) => renderCell(j),
        mapGhostCell: (j: number) => renderCell(j),
      })}
    </tr>
  );
}

const isEqualHeaderRowProp = makeCountedIsEqual('TableHeaderRowMemo');

export default React.memo(
  TableHeaderRow,
  (prevProps: Record<string, unknown>, nextProps: Record<string, unknown>) =>
    // TODO(BNCH-113857): Switch back to shallow equal once redux is enabled
    Object.keys(nextProps).every((key) => isEqualHeaderRowProp(prevProps[key], nextProps[key]))
);
